import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';

export interface VendorStatusResult {
  organization: any;
  isPendingApproval: boolean;
  isRejected: boolean;
  isActive: boolean;
  isLoading: boolean;
}

/**
 * Hook to check vendor organization status
 * Returns organization data and status flags
 */
export const useVendorStatus = (): VendorStatusResult => {
  const { userProfile } = useAuth();
  const { getEffectiveUserType, impersonatedBrand, impersonatedOrg } = useImpersonation();
  
  const effectiveUserType = getEffectiveUserType(userProfile);
  const isVendor = effectiveUserType === 'vendor_supplier';
  const isBrandImpersonation = !!impersonatedBrand?.id;
  const impersonatedOrgId = impersonatedOrg?.id || null;

  // Fetch organization data for vendors
  const { data: organizationData, isLoading: organizationLoading } = useQuery({
    queryKey: ['user-organization', impersonatedOrgId || 'self'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/organizations/me', { headers });
      if (!response.ok) {
        // Silently fail for 401/403/404 - vendor might not have an organization yet
        if (response.status === 401 || response.status === 403 || response.status === 404 || response.status === 500) {
          return null;
        }
        throw new Error(`Failed to fetch organization: ${response.status}`);
      }
      return response.json();
    },
    enabled: isVendor && !!userProfile && !isBrandImpersonation, // Only run if user is authenticated
    retry: false, // Don't retry on auth errors
  });

  const { data: approvalData, isLoading: approvalLoading } = useQuery({
    queryKey: ['vendor-approval-status', impersonatedOrgId || 'self'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/vendor-approval/my-request-details', { headers });
      if (!response.ok) {
        // Silently fail for 401/403/404/500 - might not have an approval request
        if (response.status === 401 || response.status === 403 || response.status === 404 || response.status === 500) {
          return null;
        }
        throw new Error(`Failed to fetch approval status: ${response.status}`);
      }
      return response.json();
    },
    enabled: isVendor && !!userProfile && !isBrandImpersonation,
    retry: false,
  });

  // Prefer live impersonated org payload when /organizations/me hasn't loaded yet
  const organization = organizationData?.data || (impersonatedOrgId ? impersonatedOrg : null);
  const approvalStatus = approvalData?.data?.status as string | undefined;
  const status = approvalStatus || organization?.status;
  
  // When impersonating a vendor org, treat missing status as active (not pending)
  // so dashboards can load live data before /organizations/me resolves.
  const isPendingApproval = isBrandImpersonation
    ? false
    : impersonatedOrgId
      ? status === 'pending' || status === 'pending_approval'
      : status === 'pending' || status === 'pending_approval' || !status;
  const isRejected = isBrandImpersonation ? false : status === 'rejected' || status === 'denied';
  const isActive = isBrandImpersonation
    ? true
    : impersonatedOrgId
      ? status === 'active' || status === 'approved' || !status
      : status === 'active' || status === 'approved';

  return {
    organization,
    isPendingApproval,
    isRejected,
    isActive,
    // Org impersonation already supplies organization + status; don't block on in-flight queries.
    isLoading: isBrandImpersonation || !!impersonatedOrgId
      ? false
      : organizationLoading || approvalLoading,
  };
};

